import { cn } from '@/lib/utils'
import type { IdeaRole } from './tradeIdeaVocab'

/**
 * The placement instruction on a phone (PROD-3131, from the prototype's WorkbenchTouchGuide).
 *
 * On desktop the instruction rides the crosshair. A finger has no hover, and a plate drawn under
 * it is hidden by the finger itself, so on a phone the same words are a second line of the trade
 * idea strip: what to tap, the live risk or profit while a finger is on the chart, or the reason
 * the price under it is refused. A phone has no Escape key either, so the row carries one: Cancel
 * on the entry, which drops the idea, and Skip on a level, which passes on it.
 */

export interface WorkbenchIdeaTouchGuideProps {
  /** "Tap to mark your stop loss". */
  text: string
  /** Shown in place of the instruction while the price under the finger is refused. */
  refusal: string | null
  /** "Risk 41.5 pips"; null while no finger is on the chart. Never beside a refusal. */
  readout: string | null
  role: IdeaRole
  /** Absent while a placed level is being moved: there is nothing to skip. */
  onSkip?: () => void
}

export function WorkbenchIdeaTouchGuide({ text, refusal, readout, role, onSkip }: WorkbenchIdeaTouchGuideProps) {
  // The risk/reward tone, as on the crosshair plate; the entry has no loss or gain side.
  const tone = refusal || role === 'sl' ? 'var(--danger)' : role === 'tp' ? 'var(--success)' : null
  return (
    <div
      data-idea-touch-guide
      // The chart places a level on any tap that reaches it; Skip must not also plant one.
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'flex items-center gap-2 rounded-b-md border-t px-2 py-2 text-caption font-medium',
        tone ? 'border-transparent text-white' : 'border-border bg-muted text-foreground',
      )}
      // Deepened toward black, as the crosshair plate is, so 11px white text stays readable.
      style={tone ? { background: `color-mix(in srgb, ${tone} 82%, black)`, borderTopColor: tone } : undefined}
    >
      {/* The button's twin on the other side, unseen, so the words centre on the bar itself
          rather than on what the button leaves of it. */}
      {onSkip && (
        <span aria-hidden className="invisible -my-2 flex h-11 shrink-0 items-center px-2">
          {role === 'entry' ? 'Cancel' : 'Skip'}
        </span>
      )}
      <span className="min-w-0 flex-1 text-center">
        {refusal ?? text}
        {!refusal && readout && (
          <>
            {' · '}
            {/* Kept whole: a ratio split across two lines reads as two numbers. */}
            <span
              className={cn('whitespace-nowrap font-normal', tone ? 'text-white/80' : 'text-muted-foreground')}
              data-idea-readout
            >
              {readout}
            </span>
          </>
        )}
      </span>
      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className={cn(
            // A real thumb target, grown into the row's padding rather than making the row taller.
            '-my-2 flex h-11 shrink-0 cursor-pointer items-center rounded-md px-2 transition-colors',
            tone ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          {role === 'entry' ? 'Cancel' : 'Skip'}
        </button>
      )}
    </div>
  )
}
